import { UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useMenu } from 'src/hooks';
import { DataTestID, lang } from 'src/utils';

import { Menu } from '../menu/Menu';
import type { SubheaderWithSortingProps } from './SubHeaderWithSorting.interface';
import { SORT_DIRECTION, SortOptions } from './SubHeaderWithSorting.interface';
import {
  ActionsContainer,
  HeaderContainer,
  StyledLabel,
  StyledPageSizeButton,
  StyledSortButton,
  StyledTypography,
  SubHeaderContainer,
  TitleContainer,
} from './SubHeaderWithSorting.styled';

export const SubHeaderWithSorting = (props: SubheaderWithSortingProps) => {
  const sortMenu = useMenu();
  const pageSizeMenu = useMenu();
  const orderVal =
    props.selectedSortOption.order === SORT_DIRECTION.DESC
      ? SORT_DIRECTION.ASC
      : SORT_DIRECTION.DESC;
  const nameIdentifier =
    props.title === 'Products'
      ? SortOptions.ALPHABETICALLY_DISPLAY
      : `*${SortOptions.ALPHABETICALLY}`;

  const sortItems = [
    {
      label: 'Date created',
      isSelected: props.selectedSortOption.option === SortOptions.DATE_CREATED,
      onClick: () => {
        sortMenu.onCloseMenu();
        props.onSortChange({
          option: SortOptions.DATE_CREATED,
          order: orderVal,
        });
      },
    },
    {
      label: 'A-Z',
      isSelected: props.selectedSortOption.option === nameIdentifier,
      onClick: () => {
        sortMenu.onCloseMenu();
        props.onSortChange({
          option: nameIdentifier as SortOptions,
          order: orderVal,
        });
      },
    },
    {
      label: 'Last modified',
      isSelected: props.selectedSortOption.option === SortOptions.LAST_MODIFIED,
      onClick: () => {
        sortMenu.onCloseMenu();
        props.onSortChange({
          option: SortOptions.LAST_MODIFIED,
          order: SORT_DIRECTION.DESC,
        });
      },
    },
    ...(props.title === 'Layouts'
      ? [
          {
            label: 'Status',
            isSelected: props.selectedSortOption.option === SortOptions.STATUS,
            onClick: () => {
              sortMenu.onCloseMenu();
              props.onSortChange({
                option: SortOptions.STATUS,
                order: orderVal,
              });
            },
          },
        ]
      : []),
  ];

  const pageSizeItems = [
    {
      label: '20',
      isSelected: props.pageSize === 20,
      onClick: () => {
        pageSizeMenu.onCloseMenu();
        props.onPageSizeChange?.(20);
      },
    },
    {
      label: '50',
      isSelected: props.pageSize === 50,
      onClick: () => {
        pageSizeMenu.onCloseMenu();
        props.onPageSizeChange?.(50);
      },
    },
    {
      label: '100',
      isSelected: props.pageSize === 100,
      onClick: () => {
        pageSizeMenu.onCloseMenu();
        props.onPageSizeChange?.(100);
      },
    },
  ];

  const shouldDisplayResultInfo =
    props.numResults != null && props.totalCount != null;

  const getResultRangeText = () => {
    if (props.numResults == null || props.totalCount == null) return '';

    if (props.page && props.pageSize && props.numResults > 0) {
      const start = (props.page - 1) * props.pageSize + 1;
      const end = Math.min(start + props.numResults - 1, props.totalCount);

      return `${lang.results}: ${start}-${end}/${props.totalCount}`;
    }

    return `${lang.results}: ${props.numResults}/${props.totalCount}`;
  };

  const resultCountText = getResultRangeText();

  return (
    <SubHeaderContainer alignItems="align-start" flexDirection="column">
      <Typography variant="h5" component="h2">
        <HeaderContainer>
          <TitleContainer>{props.title}</TitleContainer>
          <ActionsContainer>
            {props.isReplacementComponent && (
              <StyledSortButton
                variant="text"
                endIcon={<UnfoldMoreIcon />}
                disableRipple
                onClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) => sortMenu.onOpenMenu(event.currentTarget)}>
                {lang.sort}
              </StyledSortButton>
            )}
          </ActionsContainer>
        </HeaderContainer>
      </Typography>
      {!props.isReplacementComponent && (
        <Box display="flex" alignItems="center" mt={1}>
          <Box sx={{ flex: 1 }}>
            {shouldDisplayResultInfo && (
              <StyledTypography>{`${resultCountText}`}</StyledTypography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={3}>
            {props.numOfSelected ? (
              <StyledTypography>{`${props.numOfSelected} ${lang.selectedText}`}</StyledTypography>
            ) : null}
            {props.title === lang.assemblies && (
              <StyledLabel
                disabled={(props.numResults ?? 0) === 0}
                data-testid={DataTestID.SubHeaderWithSortingStyledLabel}
                onClick={
                  (props.numResults ?? 0) === 0
                    ? undefined
                    : props.onSelectAllClick
                }>
                {props.isSelectedAll ? lang.unSelectAll : lang.selectAll}
              </StyledLabel>
            )}

            {props.pageSize && props.onPageSizeChange && (
              <StyledPageSizeButton
                variant="text"
                endIcon={<UnfoldMoreIcon />}
                disableRipple
                onClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) => pageSizeMenu.onOpenMenu(event.currentTarget)}>
                <Box
                  component="span"
                  display="flex"
                  alignItems="center"
                  gap="10px">
                  <span>Items per page</span>
                  <span>{props.pageSize}</span>
                </Box>
              </StyledPageSizeButton>
            )}

            <StyledSortButton
              variant="text"
              endIcon={<UnfoldMoreIcon />}
              disableRipple
              onClick={(
                event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              ) => sortMenu.onOpenMenu(event.currentTarget)}>
              {lang.sort}
            </StyledSortButton>
          </Box>
        </Box>
      )}
      <Menu
        anchorEl={sortMenu.anchorEl}
        items={sortItems}
        onClose={sortMenu.onCloseMenu}
      />
      {props.pageSize && props.onPageSizeChange && (
        <Menu
          anchorEl={pageSizeMenu.anchorEl}
          items={pageSizeItems}
          onClose={pageSizeMenu.onCloseMenu}
        />
      )}
    </SubHeaderContainer>
  );
};
